import React from "react";
import PieChart from "components/charts/PieChart";
import Card from "components/card";
import { useRelatorios } from "context/RelatorioContext";
import { BsExclamationDiamond } from "react-icons/bs";

interface Props {
  selectedDate: string;
  selectedUnidadeId: string | "Todas";
  selectedPeriodo: string;
}

export default function RelatorioPesquisa({ selectedDate, selectedUnidadeId, selectedPeriodo }: Props) {
  const { relatorios } = useRelatorios();
  const faltaFiltro = selectedUnidadeId === "Todas" || !selectedDate || selectedPeriodo === "Todos";

  let muitoSatisfeito = 0;
  let satisfeito = 0;
  let regular = 0;
  let insatisfeito = 0;
  let muitoInsatisfeito = 0;
  let tituloPesquisa = "";

  if (!faltaFiltro) {
    const filtrados = relatorios.filter(r => {
      const matchDate = r.Data === selectedDate;
      const matchUnidade = r.dispositivoId === selectedUnidadeId;
      const matchPeriodo = r.expediente === selectedPeriodo;
      return matchDate && matchUnidade && matchPeriodo;
    });

    filtrados.forEach(r => {
      if (r.pesquisa) {
        if (!tituloPesquisa) {
          tituloPesquisa = r.pesquisa.titulo || "Sem Título";
        }

        let avaliacoesObj: Record<string, number> = {};

        if (r.pesquisa.avaliacoes && typeof r.pesquisa.avaliacoes === 'object') {
          avaliacoesObj = r.pesquisa.avaliacoes;
        } else {
          Object.entries(r.pesquisa).forEach(([key, value]) => {
            if (key.startsWith('avaliacoes.')) {
              const criterio = key.replace('avaliacoes.', '');
              avaliacoesObj[criterio] = Number(value);
            }
          });
        }

        muitoSatisfeito += avaliacoesObj['MuitoSatisfeito'] || 0;
        satisfeito += avaliacoesObj['Satisfeito'] || 0;
        regular += avaliacoesObj['Regular'] || 0;
        insatisfeito += avaliacoesObj['Insatisfeito'] || 0;
        muitoInsatisfeito += avaliacoesObj['MuitoInsatisfeito'] || 0;
      }
    });
  }

  const totalAvaliacoes = muitoSatisfeito + satisfeito + regular + insatisfeito + muitoInsatisfeito;
  const semDados = totalAvaliacoes === 0;
  const pieChartData = [muitoSatisfeito, satisfeito, regular, insatisfeito, muitoInsatisfeito];
  const pieChartOptions = {
    labels: ["Muito Satisfeito", "Satisfeito", "Regular", "Insatisfeito", "Muito Insatisfeito"],
    colors: ["#6FBF44", "#A8B041", "#FFC051", "#FF4B38", "#CC2435"],
    chart: { type: "pie" },
    states: { hover: { filter: { type: "none" } } },
    legend: { show: false },
    dataLabels: { enabled: false },
    tooltip: { 
      enabled: true, 
      y: { 
        formatter: (value: number) => {
          if (totalAvaliacoes === 0) return "0%";
          const percentual = ((value / totalAvaliacoes) * 100).toFixed(1).replace('.', ',');
          return `${percentual}%`;
        } 
      } 
    },
  };

  const legendas = [
    { label: "Muito Satisfeito", valor: muitoSatisfeito, cor: "bg-[#6FBF44]" },
    { label: "Satisfeito", valor: satisfeito, cor: "bg-[#A8B041]" },
    { label: "Regular", valor: regular, cor: "bg-[#FFC051]" },
    { label: "Insatisfeito", valor: insatisfeito, cor: "bg-[#FF4B38]" },
    { label: "Muito Insatisfeito", valor: muitoInsatisfeito, cor: "bg-[#CC2435]" },
  ];

  return (
    <Card extra="!p-[20px] text-center h-full flex flex-col">
      <h4 className="text-lg font-bold text-navy-700 self-start mb-4">Pesquisa de Satisfação</h4>

      {faltaFiltro || semDados ? (
        <div className="flex flex-col items-center justify-center h-full text-gray-500 mb-auto">
          <div className="mt-2 flex items-center justify-center rounded-full bg-lightPrimary p-[26px] text-5xl font-bold text-[#CF4949]">
            <BsExclamationDiamond />
          </div>
          <h3 className="text-xl font-bold text-navy-700 my-3">Nada Aqui</h3>
          <p className="text-center text-sm text-gray-600 px-4">
            {faltaFiltro 
              ? <>Selecione uma <span className="font-semibold">Unidade</span> e um <span className="font-semibold">Período</span> no filtro para visualizar.</>
              : "Nenhuma avaliação encontrada para este período."}
          </p>
        </div>
      ) : (
        <>
            <div className="mb-auto flex h-[220px] w-full items-center justify-center">
              <PieChart 
                key={pieChartData.join("-")}
                chartOptions={pieChartOptions} 
                chartData={pieChartData} 
            />
            <div className="flex w-1/3 items-center justify-center px-2">
              <p className="text-base font-medium text-navy-700 text-left">
                {tituloPesquisa}
              </p>
            </div>
          </div>

          <div className="flex flex-wrap justify-center gap-4 rounded-2xl px-4 py-4 shadow-2xl shadow-shadow-500 mt-4">
            {legendas.map((item, index) => (
              <div key={index} className="flex flex-col items-center justify-center">
                <div className="flex items-center justify-center">
                  <div className={`h-2 w-2 rounded-full ${item.cor}`} />
                  <p className="ml-1 text-sm font-normal text-gray-600">{item.label}</p>
                </div>
                <p className="mt-px text-lg font-bold text-navy-700">
                  {item.valor}
                </p>
              </div>
            ))}
          </div>
        </>
      )}
    </Card>
  );
}
